import { DatasourceManager } from "../../module/DatasourceManager";
import {ColorThematicStyler} from "../../styler/ColorThematicStyler";
import { SizeThematicStyler } from "../../styler/SizeThematicStyler";
import {
    getLayerFilter, isBubbleLayer,
    isHeatMapDisabledXorIsOverlayPinEnabled,
} from "../azure/AzureLayer";
import {BubbleLayerOptions, layer, source, Map} from "azure-maps-control"
import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {ColorThematic, SizeThematic} from "@esWebAPI/es/model/Thematic";
import {DataDrivenPropertyValueSpecification, ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";
import {AzureLayer, LAYER_DATA_LIMITS} from "@esWebAPI/es/map/layer/azure/AzureLayer";


export class AzureBubbleLayer implements AzureLayer {

    private readonly _map: Map;
    private readonly datasourceManager: DatasourceManager
    private readonly colorStyler: ColorThematicStyler;
    private readonly sizeStyler: SizeThematicStyler;

    private _layerView: ILayerView;
    private _layerInfo: LayerInfo;
    private _layer: layer.BubbleLayer|undefined;

    private colorThematic: ColorThematic|undefined;
    private sizeThematic: SizeThematic|undefined;
    private layerFilter: ExpressionFilterSpecification|undefined;

    private datasourceId: string|undefined;

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo, datasourceManager: DatasourceManager,
                colorStyler: ColorThematicStyler, sizeStyler: SizeThematicStyler,
                layerFilter?: ExpressionFilterSpecification) {
        this._map = map;
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        this.datasourceManager = datasourceManager;
        this.colorStyler = colorStyler
        this.sizeStyler = sizeStyler;

        this.layerFilter = layerFilter;
    }

    /**
     * We will adjust the radius based on the dataset size as what makes sense for 100k points does not for 1k.
     * @private
     */
    private getRadius(): DataDrivenPropertyValueSpecification<number> {
        return this.sizeStyler.getRadius(this._layerView, this._layerInfo, this.sizeThematic);
    }

    /**
     * Add or remove stroke line depending on number of points and bubble radius.
     */
    private getStrokeWidth(options: BubbleLayerOptions): DataDrivenPropertyValueSpecification<number> {
        const featCount = this._layerInfo.featureInfo.count;
        if ((featCount > LAYER_DATA_LIMITS.MIN_FEAT_COUNT_FOR_OUTLINE) &&
            (Array.isArray(options.radius) && options.radius.length > 4 && (options.radius[4] as number) < LAYER_DATA_LIMITS.MIN_BUBBLE_SIZE_FOR_OUTLINE) ||
            (typeof options.radius == 'number' && options.radius < LAYER_DATA_LIMITS.MIN_BUBBLE_SIZE_FOR_OUTLINE)) {
            return 0;
        }
        return 1;
    }

    private getOptions(): BubbleLayerOptions {
        let options =  {} as BubbleLayerOptions;

        if (this.datasourceId && this.datasourceManager.isPbf(this.datasourceId)) {
            options.sourceLayer = 'es';
        }

        const opacity = this.colorStyler.getOpacity(this._layerView);

        options.color = this.colorStyler.getHexExpression(this._layerView, this.colorThematic);
        options.radius = this.getRadius();
        options.strokeWidth = this.getStrokeWidth(options);
        options.opacity = opacity;
        options.strokeOpacity = opacity;
        options.strokeColor = es.util.localStorage.get("es.bubbleOutlineColor") ?? "#424040";
        options.visible = this._layerView.enabled && isHeatMapDisabledXorIsOverlayPinEnabled(this._layerView) && isBubbleLayer(this._layerView, this._layerInfo);
        options.filter = getLayerFilter(this._layerView, this.layerFilter);

        return options;
    }

    private async updateThematics(): Promise<void> {
        this.colorThematic = await this.colorStyler.getThematic(this._layerView);
        this.sizeThematic = await this.sizeStyler.getThematic(this._layerView);
    }

    public async create(datasource: source.Source, colorThematic?: ColorThematic, sizeThematic?: SizeThematic) {
        this.datasourceId = datasource.getId();
        this.colorThematic = colorThematic;
        this.sizeThematic = sizeThematic;
        this._layer = new layer.BubbleLayer(datasource.getId(), this.getLayerId(), this.getOptions());
        this._map.layers.add(this._layer);
    }

    public async recreate(layerView: ILayerView, layerInfo: LayerInfo, datasource: source.Source, colorThematic?: ColorThematic, sizeThematic?: SizeThematic) {
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        return this.create(datasource, colorThematic, sizeThematic);
    }

    public async update(layerView: ILayerView, layerInfo: LayerInfo): Promise<void> {
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        await this.updateThematics();
        let options = this.getOptions();
        this._layer?.setOptions(options);
    }

    public remove(): void {
        const layerId = this.getLayerId();
        if (this._map.layers.getLayerById(layerId)) {
            this._map.layers.remove(layerId);
        }
        this._layer = undefined;
    }

    public hide() {
        this._layer?.setOptions({visible: false});
    }

    public show() {
        this._layer?.setOptions({visible: true});
    }

    public setLayerFilter(filter?: ExpressionFilterSpecification) {
        this.layerFilter = filter;
    }

    public hideFeature(featureId: number) {
        const opacity = ['case',
            ['==', ['id'], featureId],
            0,
            this.colorStyler.getOpacity(this._layerView)
        ] as DataDrivenPropertyValueSpecification<number>;
        this._layer?.setOptions({opacity: opacity, strokeOpacity: opacity});
    }

    public showFeatures() {
        const opacity = this.colorStyler.getOpacity(this._layerView);
        this._layer?.setOptions({opacity: opacity, strokeOpacity: opacity});
    }

    public getLayerId(): string {
        return "bubble_" + this._layerView.id + "_" + this.datasourceId;
    }
}